// src/components/SymbolAuswahl.jsx
// Auswahl des Stationssymbols (Plakat-Druck): nach Spieltyp gruppiert, mit
// deutschen Namen. Gemeinsam fuer „Station anlegen“ und „Station bearbeiten“.
import { useState } from "react";
import * as Icons from "lucide-react";
import { ICON_GRUPPEN, ICON_NAMEN } from "../utils/stationIcons";

function Kachel({ name, label, selected, disabled, onClick }) {
  const IconComp = Icons[name];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-start gap-1 p-2 rounded-lg border transition text-center ${
        selected
          ? "bg-primary-500 text-secondary-950 border-primary-400"
          : "bg-secondary-800 text-gray-200 border-secondary-700"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-secondary-700"} ${
        selected && !disabled ? "hover:bg-primary-500" : ""
      }`}
    >
      {IconComp && <IconComp className="w-6 h-6" />}
      <span className="text-xs leading-tight">{label}</span>
    </button>
  );
}

function Pfeil({ offen }) {
  return (
    <Icons.ChevronDown
      className={`w-4 h-4 flex-none transition-transform ${offen ? "rotate-180" : ""}`}
      aria-hidden="true"
    />
  );
}

/**
 * Zugeklappt zeigt die Auswahl nur das gewaehlte Symbol; aufgeklappt sind die
 * Gruppen selbst wieder Akkordeons (jeweils eine offen, zunaechst die mit dem
 * gewaehlten Symbol).
 *
 * @param value     gewaehlter Icon-Name
 * @param onChange  (name) => void
 * @param disabled  nur ansehen (nicht aufklappbar)
 * @param hinweis   optionaler Text unter der Ueberschrift (z. B. „Vorschlag aus dem Namen“)
 */
export default function SymbolAuswahl({ value, onChange, disabled = false, hinweis = "" }) {
  const [offen, setOffen] = useState(false);
  const [offeneGruppe, setOffeneGruppe] = useState(null);

  const gruppeDesSymbols = ICON_GRUPPEN.find((g) => g.icons.some((i) => i.name === value))?.label;
  const aktiveGruppe = offeneGruppe ?? gruppeDesSymbols ?? ICON_GRUPPEN[0].label;

  // Aeltere Stationen koennen ein Symbol tragen, das nicht in der Liste steht.
  const ausserhalb = value && !ICON_NAMEN.includes(value) && Icons[value] ? value : null;

  const aktuell = ICON_GRUPPEN.flatMap((g) => g.icons).find((i) => i.name === value);
  const AktuellesIcon = Icons[value];

  return (
    <div>
      <p className="text-sm text-secondary-400">Symbol für das Stationsplakat</p>
      {hinweis && <p className="text-xs text-primary-400">{hinweis}</p>}

      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        disabled={disabled}
        aria-expanded={offen}
        className={`mt-2 w-full flex items-center gap-3 p-2 rounded-lg border bg-secondary-800 border-secondary-700 text-gray-200 text-left ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-secondary-700"
        }`}
      >
        {AktuellesIcon && <AktuellesIcon className="w-6 h-6 flex-none" />}
        <span className="flex-1 text-sm">{aktuell?.label || value}</span>
        {!disabled && (
          <>
            <span className="text-xs text-secondary-400">{offen ? "Schließen" : "Ändern"}</span>
            <Pfeil offen={offen} />
          </>
        )}
      </button>

      {offen && !disabled && (
        <div role="radiogroup" aria-label="Symbol" className="mt-2 space-y-1">
          {ausserhalb && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 pb-1">
              <Kachel name={ausserhalb} label={`${ausserhalb} (bisher)`} selected onClick={() => {}} />
            </div>
          )}
          {ICON_GRUPPEN.map((gruppe) => {
            const aufgeklappt = gruppe.label === aktiveGruppe;
            return (
              <div key={gruppe.label} className="rounded-lg border border-secondary-700">
                <button
                  type="button"
                  onClick={() => setOffeneGruppe(aufgeklappt ? "" : gruppe.label)}
                  aria-expanded={aufgeklappt}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-200 hover:bg-secondary-800 rounded-lg"
                >
                  <span>{gruppe.label}</span>
                  <Pfeil offen={aufgeklappt} />
                </button>
                {aufgeklappt && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-2 pt-0">
                    {gruppe.icons.map((icon) => (
                      <Kachel
                        key={icon.name}
                        name={icon.name}
                        label={icon.label}
                        selected={value === icon.name}
                        onClick={() => onChange(icon.name)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
